/**
 * Task Force's analytics (30 Oct 2026): the one list of events, their parameters and the user properties. The website
 * (Google Analytics 4, gtag) and the phone app (Firebase Analytics) send exactly these to one GA4 property, so a person's
 * actions read the same on both. docs/ANALYTICS.md is the plan and the rules; this file is the contract, checked by
 * analytics.test.ts. A copy lives in apps/mobile/src/lib/analyticsEvents.ts (the app is outside the workspaces): the
 * test keeps them identical.
 *
 * Rules (docs/ANALYTICS.md): an event is something a person did and the server accepted (or a screen they saw); names
 * are object_action in snake_case and never change meaning; parameters are enums, counts, ids and the organization's
 * own names (spaces, statuses, fields, types), never what people wrote (titles, comments, search words, file names) and
 * never who someone is (names, emails): the person is the user id, their account's id.
 */
/** Every event, what it means and its parameters (docs and the GA4 set-up read this). */
export const TF_EVENTS = {
    login: { about: 'Signed in', params: ['method'] },
    login_failed: { about: 'A sign-in that did not go through, and why', params: ['method', 'reason'] },
    page_view: {
        about: 'A Task Force page (website) or screen (app: screen_view)',
        params: ['surface', 'view', 'space_name'],
    },
    task_open: {
        about: 'A task opened, from where and how',
        params: ['surface', 'entry', 'task_ref', 'inbox_reason'],
    },
    task_create: {
        about: 'A task made: new, duplicated or from a template',
        params: ['surface', 'method', 'is_subtask', 'task_type', 'space_name'],
    },
    task_update: {
        about: 'Anything about a task but its status',
        params: ['surface', 'field', 'field_name', 'count'],
    },
    task_status_change: {
        about: 'A task moved to another status',
        params: ['surface', 'to_status', 'status_group', 'count'],
    },
    status_fields_ask: {
        about: 'A status needed fields first: asked for, saved or not',
        params: ['surface', 'to_status', 'outcome', 'count'],
    },
    task_delete: { about: 'Tasks moved to the Trash', params: ['surface', 'count'] },
    task_follow: { about: 'Following a task, or not', params: ['surface', 'following'] },
    share: { about: "A task's link copied or shared", params: ['surface', 'method', 'content_type', 'count'] },
    comment_add: {
        about: 'A comment or reply written',
        params: ['surface', 'is_reply', 'is_assigned', 'mentions'],
    },
    comment_update: {
        about: 'A comment edited, deleted, reacted to, assigned or resolved',
        params: ['surface', 'action'],
    },
    attachment_add: { about: 'Files added to a task', params: ['surface', 'count', 'file_kind'] },
    attachment_open: { about: 'A file opened', params: ['surface', 'file_kind'] },
    attachment_remove: { about: 'A file removed from a task', params: ['surface'] },
    checklist_update: { about: 'A checklist or its items changed', params: ['surface', 'action'] },
    filter_apply: {
        about: "A view's filters changed",
        params: ['surface', 'view', 'filter_count', 'filter_fields'],
    },
    view_customize: { about: 'How a view shows its tasks changed', params: ['surface', 'view', 'setting'] },
    view_save: { about: 'A saved view made, changed or removed', params: ['surface', 'action'] },
    search: { about: 'A search with results shown', params: ['surface', 'term_length', 'results_count'] },
    inbox_action: { about: 'Inbox updates marked, cleared or snoozed', params: ['surface', 'action', 'count'] },
    favorite_update: { about: 'Favorites changed', params: ['surface'] },
    space_create: { about: 'A space made', params: ['surface'] },
    push_permission: { about: 'The answer to the notifications ask', params: ['surface', 'result'] },
    push_open: { about: 'A push notification tapped (app)', params: ['kind'] },
    feedback_submit: { about: 'Feedback sent', params: ['surface'] },
    form_submit: { about: 'A public form sent', params: ['has_files'] },
    action_failed: { about: 'An action refused or failed', params: ['surface', 'failed_event', 'code'] },
};
/** Parameters that are numbers (GA4 custom metrics); the rest are text (custom dimensions). */
export const TF_METRICS = ['count', 'mentions', 'filter_count', 'term_length', 'results_count'];
/** Parameters registered as GA4 custom dimensions (event-scoped). task_ref is left for BigQuery: too many values. */
export const TF_DIMENSIONS = [
    'surface',
    'view',
    'space_name',
    'entry',
    'inbox_reason',
    'method',
    'is_subtask',
    'task_type',
    'field',
    'field_name',
    'to_status',
    'status_group',
    'following',
    'is_reply',
    'is_assigned',
    'action',
    'file_kind',
    'filter_fields',
    'setting',
    'result',
    'kind',
    'reason',
    'has_files',
    'content_type',
    'failed_event',
    'code',
    'web_version',
    'outcome',
];
export const TF_USER_PROPERTIES = ['tf_role', 'user_type', 'company', 'team'];
/** GA4's and Firebase's limits and reserved names (checked by the test, applied when sending). */
export const ANALYTICS_LIMITS = { name: 40, paramValue: 100, params: 25, userPropName: 24, userPropValue: 36 };
/**
 * Parameters as GA4 and Firebase take them: booleans as "true"/"false" (Firebase drops booleans), text cut to 100
 * characters, nothing undefined.
 */
export function analyticsParams(p) {
    const out = {};
    for (const [k, v] of Object.entries(p)) {
        if (v === undefined || v === null)
            continue;
        if (typeof v === 'boolean')
            out[k] = v ? 'true' : 'false';
        else if (typeof v === 'number')
            out[k] = Number.isFinite(v) ? v : 0;
        else
            out[k] = String(v).slice(0, ANALYTICS_LIMITS.paramValue);
    }
    return out;
}
/** User properties as GA4 and Firebase take them: values cut to 36 characters. */
export function analyticsUser(p) {
    const out = {};
    for (const [k, v] of Object.entries(p))
        out[k] = String(v ?? '').slice(0, ANALYTICS_LIMITS.userPropValue);
    return out;
}
const TASK_FIELDS = {
    title: 'title',
    description: 'description',
    priority: 'priority',
    startDate: 'start_date',
    dueDate: 'due_date',
    typeId: 'type',
    listId: 'space',
    parentId: 'parent',
};
/**
 * The event a write to Task Force's API stands for, once the server accepted it (30 Oct 2026): the website and the app
 * call this from their one request layer, so every screen that makes a change is counted the same way. Null for writes
 * that are not tracked (settings, the free trial, push registration …). `path` is the request's path (with or without
 * /api/tf and a query string); `body` what was sent.
 */
export function eventForWrite(method, path, body, ctx) {
    const m = method.toUpperCase();
    const p = path.replace(/[?#].*$/, '').replace(/^\/api\/tf(?=\/)/, '');
    const b = (body && typeof body === 'object' ? body : {});
    const surface = ctx.surface;
    const seg = p.split('/').filter(Boolean);
    const str = (v) => (typeof v === 'string' ? v : '');
    const ids = (v) => (Array.isArray(v) ? v.length : 0);
    // Tasks
    if (seg[0] === 'tasks') {
        if (m === 'POST' && seg.length === 1)
            return {
                name: 'task_create',
                params: {
                    surface,
                    method: 'new',
                    is_subtask: !!b.parentId,
                    task_type: ctx.typeName?.(str(b.typeId)),
                    space_name: ctx.spaceOfList?.(str(b.listId)),
                },
            };
        if (m === 'POST' && seg[1] === 'bulk') {
            const items = Array.isArray(b.items) ? b.items : [];
            const set = items[0]?.set ?? {};
            const count = items.length;
            if (typeof set.statusId === 'string') {
                const s = ctx.status?.(set.statusId);
                return {
                    name: 'task_status_change',
                    params: { surface: 'bulk', to_status: s?.name ?? 'unknown', status_group: s?.group ?? undefined, count },
                };
            }
            const keys = Object.keys(set).filter((k) => set[k] !== undefined);
            const people = set.people;
            const value = set.value;
            const field = keys.length > 1
                ? 'multiple'
                : people
                    ? 'people'
                    : value
                        ? 'custom_field'
                        : (TASK_FIELDS[keys[0] ?? ''] ?? 'multiple');
            const field_name = people?.fieldId
                ? ctx.peopleFieldName?.(people.fieldId)
                : value?.field
                    ? ctx.fieldName?.(value.field)
                    : undefined;
            return { name: 'task_update', params: { surface: 'bulk', field, field_name, count } };
        }
        if (m === 'POST' && seg[1] === 'bulk-delete')
            return { name: 'task_delete', params: { surface: 'bulk', count: ids(b.ids) } };
        if (seg.length === 2 && m === 'PATCH') {
            const keys = Object.keys(b).filter((k) => b[k] !== undefined && TASK_FIELDS[k]);
            if (!keys.length)
                return null;
            return {
                name: 'task_update',
                params: { surface, field: keys.length > 1 ? 'multiple' : TASK_FIELDS[keys[0]], count: 1 },
            };
        }
        if (seg.length === 2 && m === 'DELETE')
            return { name: 'task_delete', params: { surface, count: 1 } };
        switch (`${m} ${seg[2] ?? ''}`) {
            case 'POST status': {
                const s = ctx.status?.(str(b.statusId));
                return {
                    name: 'task_status_change',
                    params: { surface, to_status: s?.name ?? 'unknown', status_group: s?.group ?? undefined, count: 1 },
                };
            }
            case 'PUT people':
                return {
                    name: 'task_update',
                    params: { surface, field: 'people', field_name: ctx.peopleFieldName?.(seg[3] ?? ''), count: 1 },
                };
            case 'PUT values':
                return {
                    name: 'task_update',
                    params: { surface, field: 'custom_field', field_name: ctx.fieldName?.(seg[3] ?? ''), count: 1 },
                };
            case 'POST duplicate':
                return { name: 'task_create', params: { surface, method: 'duplicate', is_subtask: false } };
            case 'PUT watch':
                return { name: 'task_follow', params: { surface, following: b.on === true } };
            case 'POST relations':
                return { name: 'task_update', params: { surface, field: 'relation', count: 1 } };
            case 'POST checklists':
                return { name: 'checklist_update', params: { surface, action: 'create' } };
            case 'POST comments': {
                const html = str(b.body);
                return {
                    name: 'comment_add',
                    params: {
                        surface,
                        is_reply: !!b.parentId,
                        is_assigned: !!b.assigneeId,
                        mentions: (html.match(/data-type="mention"/g) ?? []).length,
                    },
                };
            }
        }
        return null;
    }
    if (seg[0] === 'templates' && seg[2] === 'use' && m === 'POST')
        return { name: 'task_create', params: { surface, method: 'template', is_subtask: false } };
    if (seg[0] === 'relations' && m === 'DELETE')
        return { name: 'task_update', params: { surface, field: 'relation', count: 1 } };
    // Comments
    if (seg[0] === 'comments') {
        if (seg.length === 2)
            return m === 'PATCH'
                ? { name: 'comment_update', params: { surface, action: 'edit' } }
                : m === 'DELETE'
                    ? { name: 'comment_update', params: { surface, action: 'delete' } }
                    : null;
        if (seg[2] === 'assignee' && m === 'PUT')
            return { name: 'comment_update', params: { surface, action: 'assign' } };
        if (seg[2] === 'resolved' && m === 'PUT')
            return { name: 'comment_update', params: { surface, action: b.resolved === false ? 'reopen' : 'resolve' } };
        if (seg[2] === 'reactions')
            return { name: 'comment_update', params: { surface, action: 'react' } };
        return null;
    }
    // Checklists and files
    if (seg[0] === 'checklists') {
        if (seg.length === 2 && m === 'PATCH')
            return { name: 'checklist_update', params: { surface, action: 'rename' } };
        if (seg.length === 2 && m === 'DELETE')
            return { name: 'checklist_update', params: { surface, action: 'delete' } };
        if (seg[2] === 'items' && m === 'POST')
            return { name: 'checklist_update', params: { surface, action: 'add_item' } };
        return null;
    }
    if (seg[0] === 'checklist-items') {
        if (m === 'DELETE')
            return { name: 'checklist_update', params: { surface, action: 'delete_item' } };
        if (m !== 'PATCH')
            return null;
        // Moving an item (position only) is not a change people mean to make.
        if (b.done === true)
            return { name: 'checklist_update', params: { surface, action: 'check' } };
        if (b.done === false)
            return { name: 'checklist_update', params: { surface, action: 'uncheck' } };
        return b.text !== undefined || b.employeeId !== undefined
            ? { name: 'checklist_update', params: { surface, action: 'edit_item' } }
            : null;
    }
    if (seg[0] === 'attachments' && m === 'DELETE')
        return { name: 'attachment_remove', params: { surface } };
    // The Inbox (and Catch up)
    if (seg[0] === 'inbox' && m === 'POST') {
        const count = ids(b.ids);
        switch (seg[1]) {
            case 'read':
                return {
                    name: 'inbox_action',
                    params: { surface, action: b.read === false ? 'mark_unread' : 'mark_read', count },
                };
            case 'clear':
                return { name: 'inbox_action', params: { surface, action: b.cleared === false ? 'restore' : 'clear', count } };
            case 'clear-all':
                return { name: 'inbox_action', params: { surface, action: 'clear_all', count: 0 } };
            case 'snooze':
                return { name: 'inbox_action', params: { surface, action: b.until ? 'snooze' : 'unsnooze', count } };
        }
        return null;
    }
    // Views, favorites, spaces, feedback
    if (seg[0] === 'views') {
        if (m === 'POST' && seg.length === 1)
            return { name: 'view_save', params: { surface, action: 'create' } };
        if (m === 'PATCH')
            return { name: 'view_save', params: { surface, action: 'update' } };
        if (m === 'DELETE')
            return { name: 'view_save', params: { surface, action: 'delete' } };
        return null;
    }
    if (seg[0] === 'favorites' && m === 'PUT')
        return { name: 'favorite_update', params: { surface } };
    if (seg[0] === 'spaces' && seg.length === 1 && m === 'POST')
        return { name: 'space_create', params: { surface } };
    if (seg[0] === 'feedback' && m === 'POST')
        return { name: 'feedback_submit', params: { surface } };
    return null;
}
/** What kind of file: for attachment_add and attachment_open. */
export function fileKind(type) {
    const t = (type ?? '').toLowerCase();
    return t.startsWith('image/')
        ? 'image'
        : t === 'application/pdf'
            ? 'pdf'
            : t.startsWith('video/')
                ? 'video'
                : 'other';
}
/** The kind of several files: one kind, or "mixed". */
export function filesKind(types) {
    const kinds = new Set(types.map(fileKind));
    return kinds.size === 1 ? [...kinds][0] : kinds.size ? 'mixed' : 'other';
}
//# sourceMappingURL=analytics.js.map